import { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContentRefresh } from "@/context/ContentContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const Field = ({ label, id, value, onChange, ...rest }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} data-testid={`content-${id}`} value={value} onChange={onChange} className="mt-2" {...rest} />
  </div>
);

export default function ContentManager() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const refresh = useContentRefresh();

  useEffect(() => {
    api.get("/admin/content").then((r) => {
      const c = r.data;
      setData({
        ...c,
        about: {
          intro: c.about?.intro || "",
          paragraphsText: (c.about?.paragraphs || []).join("\n\n"),
          areasText: (c.about?.areas || []).join("\n"),
        },
      });
    });
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setAbout = (k, v) => setData((d) => ({ ...d, about: { ...d.about, [k]: v } }));

  const save = async () => {
    setSaving(true);
    const payload = {
      name: data.name, fullName: data.fullName, role: data.role, tagline: data.tagline,
      origin: data.origin, instagram: data.instagram, instagramUrl: data.instagramUrl,
      email: data.email, phone: data.phone, location: data.location,
      about: {
        intro: data.about.intro,
        paragraphs: data.about.paragraphsText.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean),
        areas: data.about.areasText.split(/\n/).map((s) => s.trim()).filter(Boolean),
      },
    };
    try {
      await api.put("/admin/content", payload);
      toast.success("Conteúdo guardado.");
      refresh && refresh();
    } catch { toast.error("Não foi possível guardar."); }
    setSaving(false);
  };

  if (!data) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-700" /></div>;

  return (
    <div className="space-y-8" data-testid="content-manager">
      <div className="bg-white rounded-2xl border border-rose-100 p-6 lg:p-8">
        <h2 className="font-display text-2xl text-stone-900">Identidade</h2>
        <div className="grid md:grid-cols-2 gap-5 mt-6">
          <Field label="Nome (marca)" id="name" value={data.name} onChange={(e) => set("name", e.target.value)} />
          <Field label="Nome completo" id="fullName" value={data.fullName} onChange={(e) => set("fullName", e.target.value)} />
          <Field label="Especialidade" id="role" value={data.role} onChange={(e) => set("role", e.target.value)} />
          <Field label="Cidade / origem" id="origin" value={data.origin} onChange={(e) => set("origin", e.target.value)} />
          <div className="md:col-span-2">
            <Field label="Frase / lema" id="tagline" value={data.tagline} onChange={(e) => set("tagline", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-rose-100 p-6 lg:p-8">
        <h2 className="font-display text-2xl text-stone-900">Contactos</h2>
        <div className="grid md:grid-cols-2 gap-5 mt-6">
          <Field label="Email" id="email" value={data.email} onChange={(e) => set("email", e.target.value)} />
          <Field label="Telefone" id="phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} />
          <Field label="Instagram (@)" id="instagram" value={data.instagram} onChange={(e) => set("instagram", e.target.value)} />
          <Field label="Link do Instagram" id="instagramUrl" value={data.instagramUrl} onChange={(e) => set("instagramUrl", e.target.value)} />
          <div className="md:col-span-2">
            <Field label="Localização" id="location" value={data.location} onChange={(e) => set("location", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-rose-100 p-6 lg:p-8">
        <h2 className="font-display text-2xl text-stone-900">Sobre a Inês</h2>
        <div className="space-y-5 mt-6">
          <Field label="Introdução (título)" id="intro" value={data.about.intro} onChange={(e) => setAbout("intro", e.target.value)} />
          <div>
            <Label htmlFor="paras">Parágrafos (separe por uma linha em branco)</Label>
            <Textarea id="paras" data-testid="content-paragraphs" rows={6} value={data.about.paragraphsText}
              onChange={(e) => setAbout("paragraphsText", e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="areas">Áreas de intervenção (uma por linha)</Label>
            <Textarea id="areas" data-testid="content-areas" rows={8} value={data.about.areasText}
              onChange={(e) => setAbout("areasText", e.target.value)} className="mt-2" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="save-content"
          className="btn-rose inline-flex items-center gap-2 rounded-full bg-rose-800 text-white px-8 py-3 text-sm disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar alterações
        </button>
      </div>
    </div>
  );
}
